// src/react.tsx
import {
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient
} from "@tanstack/react-query";
import { createContext, useContext } from "react";
import { jsx } from "react/jsx-runtime";
var ApiContext = createContext(null);
function ApiProvider({ api, children }) {
  return /* @__PURE__ */ jsx(ApiContext.Provider, { value: api, children });
}
function useApi() {
  const api = useContext(ApiContext);
  if (!api) throw new Error("useApi must be used inside <ApiProvider>");
  return api;
}
var qk = {
  me: ["me"],
  users: ["users"],
  conversations: (filter, search) => ["conversations", filter, search],
  conversationsAll: ["conversations"],
  conversation: (id) => ["conversation", id],
  suggestions: (id) => ["suggestions", id],
  customerProfile: (id) => ["customer-profile", id],
  messages: (id) => ["messages", id],
  orders: (q) => ["orders", q],
  ordersAll: ["orders"],
  order: (id) => ["order", id],
  menu: ["menu"],
  specials: (date) => ["specials", date],
  specialsAll: ["specials"],
  campaigns: ["campaigns"],
  customers: (search) => ["customers", search],
  settings: ["settings"],
  templates: ["templates"],
  mappings: ["mappings"],
  analytics: (kind, q) => ["analytics", kind, q],
  insights: ["insights"]
};
var useMe = (enabled = true) => {
  const api = useApi();
  return useQuery({ queryKey: qk.me, queryFn: api.auth.me, enabled, retry: false });
};
function useConversations(filter, search = "") {
  const api = useApi();
  return useInfiniteQuery({
    queryKey: qk.conversations(filter, search),
    queryFn: ({ pageParam }) => api.conversations.list({ filter, search: search || void 0, before: pageParam, limit: 40 }),
    initialPageParam: void 0,
    getNextPageParam: (p) => p.nextCursor ?? void 0
  });
}
var useConversation = (id) => {
  const api = useApi();
  return useQuery({ queryKey: qk.conversation(id ?? ""), queryFn: () => api.conversations.get(id), enabled: !!id });
};
var useSuggestions = (conversationId, enabled = true) => {
  const api = useApi();
  return useQuery({
    queryKey: qk.suggestions(conversationId ?? ""),
    queryFn: () => api.conversations.suggestions(conversationId),
    enabled: !!conversationId && enabled
  });
};
function useMessages(conversationId) {
  const api = useApi();
  return useInfiniteQuery({
    queryKey: qk.messages(conversationId ?? ""),
    queryFn: ({ pageParam }) => api.conversations.messages(conversationId, { before: pageParam, limit: 50 }),
    initialPageParam: void 0,
    getNextPageParam: (p) => p.nextCursor ?? void 0,
    enabled: !!conversationId
  });
}
var useSpecials = (date) => {
  const api = useApi();
  return useQuery({ queryKey: qk.specials(date), queryFn: () => api.specials.list(date || void 0) });
};
var useCampaigns = () => {
  const api = useApi();
  return useQuery({ queryKey: qk.campaigns, queryFn: api.campaigns.list, refetchInterval: 15e3 });
};
var useCustomerSearch = (search) => {
  const api = useApi();
  return useQuery({ queryKey: qk.customers(search), queryFn: () => api.customers.search({ search: search || void 0, limit: 20 }) });
};
var useOrders = (q = {}) => {
  const api = useApi();
  return useQuery({ queryKey: qk.orders(q), queryFn: () => api.orders.list(q) });
};
var useOrder = (id) => {
  const api = useApi();
  return useQuery({ queryKey: qk.order(id ?? ""), queryFn: () => api.orders.get(id), enabled: !!id });
};
var useMenu = () => {
  const api = useApi();
  return useQuery({ queryKey: qk.menu, queryFn: api.menu.list, staleTime: 6e4 });
};
var useSettings = () => {
  const api = useApi();
  return useQuery({ queryKey: qk.settings, queryFn: api.settings.get, staleTime: 6e4 });
};
var useTemplates = () => {
  const api = useApi();
  return useQuery({ queryKey: qk.templates, queryFn: api.templates.list });
};
var useTemplateMappings = () => {
  const api = useApi();
  return useQuery({ queryKey: qk.mappings, queryFn: api.templates.mappings });
};
var useUsers = () => {
  const api = useApi();
  return useQuery({ queryKey: qk.users, queryFn: api.users.list });
};
var useAnalyticsSummary = (range) => {
  const api = useApi();
  return useQuery({ queryKey: qk.analytics("summary", range), queryFn: () => api.analytics.summary(range), staleTime: 3e4 });
};
var useTopItems = (range) => {
  const api = useApi();
  return useQuery({ queryKey: qk.analytics("topItems", range), queryFn: () => api.analytics.topItems(range), staleTime: 3e4 });
};
var useHeatmap = (range) => {
  const api = useApi();
  return useQuery({ queryKey: qk.analytics("heatmap", range), queryFn: () => api.analytics.heatmap(range), staleTime: 3e4 });
};
var useWhatsAppNumbers = () => {
  const api = useApi();
  return useQuery({ queryKey: ["whatsapp-numbers"], queryFn: () => api.whatsapp.numbers(), staleTime: 6e4 });
};
var useMenuInsights = () => {
  const api = useApi();
  return useQuery({ queryKey: qk.analytics("menu-insights", {}), queryFn: () => api.analytics.menuInsights(), staleTime: 6e4 });
};
var useCustomerProfile = (customerId) => {
  const api = useApi();
  return useQuery({ queryKey: qk.customerProfile(customerId ?? ""), queryFn: () => api.customers.profile(customerId), enabled: !!customerId, staleTime: 3e4 });
};
var useAiStats = (range) => {
  const api = useApi();
  return useQuery({ queryKey: qk.analytics("ai", range), queryFn: () => api.analytics.ai(range), staleTime: 3e4 });
};
var useTimeseries = (range) => {
  const api = useApi();
  return useQuery({ queryKey: qk.analytics("timeseries", range), queryFn: () => api.analytics.timeseries(range), staleTime: 3e4 });
};
var useInsights = () => {
  const api = useApi();
  return useQuery({ queryKey: qk.insights, queryFn: api.analytics.insights });
};
function useSendReply(conversationId) {
  const api = useApi();
  return useMutation({ mutationFn: (input) => api.conversations.reply(conversationId, input) });
}
function useTransitionOrder() {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (v) => api.orders.transition(v.id, v.to, v.reason),
    onSuccess: (o) => upsertOrder(qc, o)
  });
}
function upsertMessage(qc, m) {
  qc.setQueryData(qk.messages(m.conversationId), (data) => {
    if (!data) return data;
    const exists = data.pages.some((p) => p.data.some((x) => x.id === m.id));
    const pages = exists ? data.pages.map((p) => ({ ...p, data: p.data.map((x) => x.id === m.id ? m : x) })) : [{ ...data.pages[0], data: [m, ...data.pages[0].data] }, ...data.pages.slice(1)];
    return { ...data, pages };
  });
}
function upsertConversation(qc, c) {
  qc.setQueryData(qk.conversation(c.id), c);
  void qc.invalidateQueries({ queryKey: qk.conversationsAll });
}
function upsertOrder(qc, o) {
  qc.setQueryData(qk.order(o.id), (old) => ({ ...old, ...o }));
  void qc.invalidateQueries({ queryKey: qk.ordersAll });
}
function realtimeCacheHandlers(qc) {
  return {
    onMessage: (m) => {
      upsertMessage(qc, m);
      if (m.type !== "reaction") void qc.invalidateQueries({ queryKey: qk.suggestions(m.conversationId) });
    },
    onAiSuggestions: (d) => qc.setQueryData(qk.suggestions(d.id), { runId: d.runId, suggestions: d.suggestions }),
    onMessageStatus: (m) => upsertMessage(qc, m),
    onConversation: (c) => upsertConversation(qc, c),
    onOrderCreated: (o) => upsertOrder(qc, o),
    onOrderUpdated: (o) => upsertOrder(qc, o),
    onAiDraft: (o) => upsertOrder(qc, o),
    onTemplateStatus: () => void qc.invalidateQueries({ queryKey: qk.templates })
  };
}
export {
  ApiProvider,
  qk,
  realtimeCacheHandlers,
  upsertOrder,
  useAiStats,
  useAnalyticsSummary,
  useApi,
  useCampaigns,
  useConversation,
  useConversations,
  useCustomerProfile,
  useCustomerSearch,
  useHeatmap,
  useInsights,
  useMe,
  useMenu,
  useMenuInsights,
  useMessages,
  useOrder,
  useOrders,
  useSendReply,
  useSettings,
  useSpecials,
  useSuggestions,
  useTemplateMappings,
  useTemplates,
  useTimeseries,
  useTopItems,
  useTransitionOrder,
  useUsers,
  useWhatsAppNumbers
};
